import * as Maths from "oc-commons/Maths";
import { PixelAccessor, type RGBA } from "./PixelAccessor";
import * as _ from "lodash";
import * as Doms from "oc-ui/Doms";

export type CanvasImageSource =
  | HTMLImageElement
  | HTMLCanvasElement
  | ImageBitmap
  | OffscreenCanvas;

export type { RGBA };

export interface MinMaxRGBA {
  min: RGBA;
  max: RGBA;
}

/**
 * Statische Utility-Klasse zum Thema Image und Canvas
 */
export class Images {
  /**
   * erzeugt ein Canvas von einem Image
   * @param image
   */
  static canvasFromImage(
    image: CanvasImageSource | null | undefined,
    width?: number,
    height?: number,
  ): HTMLCanvasElement | null | undefined {
    if (image == null) {
      return image as null | undefined;
    }
    width = width === undefined ? image.width : width;
    height = height === undefined ? image.height : height;
    const canvas = Images.canvas(width, height);
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(image, 0, 0, width, height);
    return canvas;
  }

  /**
   * erzeugt ein Image von einem Canvas
   */
  static convertCanvasToImage(canvas: HTMLCanvasElement): HTMLImageElement {
    const image = new Image();
    image.src = canvas.toDataURL("image/png");
    return image;
  }

  /**
   * erzeugt ein Canvas
   */
  static canvas(width: number, height: number): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    canvas.setAttribute("width", String(width));
    canvas.setAttribute("height", String(height));
    return canvas;
  }

  /**
   * lädt ein Image mit einem Promise
   * @returns {Promise}
   */
  static loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.addEventListener("load", () => resolve(img));
      img.addEventListener("error", () => {
        reject(new Error(`Failed to load image's URL: ${url}`));
      });
      img.src = url;
    });
  }

  /**
   * prüft, ob ein object ein Canvas ist
   */
  static isCanvas(object: unknown): object is HTMLCanvasElement {
    return object != null && (object as HTMLCanvasElement).getContext !== undefined;
  }

  /**
   * Malt einen Shadow um die nicht-transparenten Teile eines Bildes
   */
  static dropShadow(
    image: CanvasImageSource | null | undefined,
    blur: number = 20,
    x: number = 2,
    y: number = 2,
    color: string = "rgba( 0, 0, 0, 1 )",
  ): HTMLCanvasElement | null | undefined {
    if (!image) {
      return image as null | undefined;
    }

    const canvas = Images.canvas(image.width, image.height);
    const ctx = canvas.getContext("2d")!;
    ctx.shadowColor = color;
    ctx.shadowOffsetX = x;
    ctx.shadowOffsetY = y;
    ctx.shadowBlur = blur;
    ctx.drawImage(image, 0, 0);

    return canvas;
  }

  /**
   * Malt ein transparentes Bild über einen einfarbigen Hintergrund
   */
  static overUnicolorBackground(
    image: CanvasImageSource | null | undefined,
    backgroundColor: string = "rgb(255,255,255)",
  ): HTMLCanvasElement | null | undefined {
    if (!image) {
      return image as null | undefined;
    }
    const canvas = Images.canvas(image.width, image.height);
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, image.width, image.height);
    ctx.drawImage(image, 0, 0);

    return canvas;
  }

  /**
   * verkleinert ein Bild
   */
  static scaleDown(
    image: CanvasImageSource | null | undefined,
    sizeFactor: number,
  ): HTMLCanvasElement | null | undefined {
    if (!image) {
      return image as null | undefined;
    }
    const w = Math.round(image.width * sizeFactor);
    const h = Math.round(image.width * sizeFactor);
    const canvas = Images.canvas(w, h);
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(image, 0, 0, w, h);
    return canvas;
  }

  /**
   * prüft, ob ein Canvas transparente Pixel enthält
   */
  static hasTransparency(canvas: HTMLCanvasElement, minOpacity: number = 255): boolean {
    const ctx = canvas.getContext("2d")!;
    const imgdata = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const length = imgdata.data.length / 4;
    for (let i = 0; i < length; ++i) {
      if (imgdata.data[i * 4 + 3] < minOpacity) {
        return true;
      }
    }
    return false;
  }

  /**
   * gibt den tatsächlich verwendeten Farbbereich des Canvas zurück als min- und max- Color (je Int[4] entspricht RGBA)
   * Kann benutzt werden, um die Einfarbigkeit eines Images oder einzelner Farbkanäle zu prüfen.
   */
  static getMinMaxRGBA(canvas: HTMLCanvasElement): MinMaxRGBA {
    const pixels = new PixelAccessor(canvas);
    const min: RGBA = pixels.createRGBA().map(() => Number.POSITIVE_INFINITY) as RGBA;
    const max: RGBA = pixels.createRGBA().map(() => Number.NEGATIVE_INFINITY) as RGBA;
    const rgba = pixels.createRGBA();
    pixels.forRaster((x, y) => {
      pixels.getRGBA(x, y, rgba);
      for (let i = 0; i < 4; i++) {
        min[i] = Math.min(min[i], rgba[i]);
        max[i] = Math.max(max[i], rgba[i]);
      }
    });
    return { min, max };
  }

  /**
   *
   * @param img
   * @returns {boolean}
   */
  static isImageLoaded(img: HTMLImageElement | null | undefined): boolean {
    if (img == null) {
      return false;
    }

    // During the onload event, IE correctly identifies any images that
    // weren't downloaded as not complete. Others should too. Gecko-based
    // browsers act like NS4 in that they report this incorrectly.
    if (!img.complete) {
      return false;
    }

    // However, they do have two very useful properties: naturalWidth and
    // naturalHeight. These give the true size of the image. If it failed
    // to load, either of these should be zero.
    if (img.naturalWidth === 0) {
      return false;
    }

    // No other way of checking: assume it's ok.
    return true;
  }

  /**
   *
   * @param img
   * @param callback
   */
  static whenImageLoaded(
    img: HTMLImageElement | null | undefined,
    callback: (img?: HTMLImageElement) => void,
  ): void {
    if (img == null) {
      console.log("img is null", img);
      return;
    }
    if (Images.isImageLoaded(img)) {
      callback(img);
    } else {
      if (img.addEventListener) {
        console.log("wait for img.addEventListener load ", img);
        img.addEventListener("load", () => callback(img), true);
      } else {
        console.warn("no image eventlistener - callback directly");
        callback();
      }
    }
  }

  /**
   *
   * @param img
   * @returns {Promise<any>}
   */
  static whenImgLoadedPromise(img: HTMLImageElement | null): Promise<HTMLImageElement> | null {
    if (img == null) return null;
    return new Promise((resolve, reject) => {
      if (Images.isImageLoaded(img)) {
        console.log(`:: isImageLoaded: ${img} is loaded`);
        resolve(img);
      } else {
        img.addEventListener("load", () => resolve(img));
        img.addEventListener("error", () =>
          reject(new Error(`Failed to load image's URL: ${img.src}`)),
        );
      }
    });
  }

  /**
   * resized ein Image so, dass es in jeder Dimension maximal 1024 Pixel misst, und in jedem Fall ein PowerOfTwo ist, und gibt es als Canvas zurück
   */
  static textureSaneResize(
    image: CanvasImageSource | null | undefined,
    maxSize: number = 1024,
  ): HTMLCanvasElement | null | undefined {
    if (image == null) {
      return null;
    }
    const fit = (v: number) => Math.min(maxSize, Maths.ceilToNextPowerOf(v, 2));
    return Images.canvasFromImage(image, fit(image.width), fit(image.height));
  }

  /**
   * Zeigt ein Image für Debugzwecke an.
   * Zeigt ein Image in einem neuen Window an. Das Image kann ein Image-Element,
   * ein Canvas, eine URL oder einen base64-enkodiertes Image sein.
   * @param imageData
   * @returns imageData
   */
  static showImageInWindow<
    T extends string | Blob | HTMLImageElement | HTMLCanvasElement | null | undefined,
  >(imageData: T, width?: number, height?: number): T {
    const openWindow = (img: HTMLImageElement) => {
      const options =
        "scrollbars=0, toolbar=0, width=" +
        (width ?? img.width) +
        ", height=" +
        (height ?? img.height);
      console.log("options", options);
      const newWindow = window.open("", "image", options);
      if (newWindow) {
        newWindow.document.write(img.outerHTML);
      } else {
        const div = Doms.createElementFromHTML(
          '<div style="position:absolute;z-index:1000;top:10px;left:10px;">',
        ) as HTMLDivElement;
        div.appendChild(img);
        document.body.appendChild(div);
      }
    };

    const blobToDataURL = (blob: Blob, callback: (dataURL: string) => void) => {
      const reader = new FileReader();
      reader.onload = function (event) {
        callback(event.target!.result as string);
      };
      reader.readAsDataURL(blob);
    };

    if (_.isString(imageData)) {
      const myImage = new Image();
      myImage.src = imageData;
      myImage.style.border = "none";
      myImage.style.outline = "none";
      myImage.style.position = "fixed";
      myImage.style.left = "0";
      myImage.style.top = "0";
      myImage.onload = () => {
        openWindow(myImage);
      };
    } else if (imageData != null) {
      if (imageData instanceof Blob) {
        blobToDataURL(imageData, (dataURL) => {
          Images.showImageInWindow(dataURL, width, height);
        });
      } else if (imageData instanceof HTMLImageElement) {
        imageData.onload = () => {
          openWindow(imageData);
        };
      } else {
        Images.showImageInWindow(
          (imageData as HTMLCanvasElement).toDataURL("image/jpeg"),
          width,
          height,
        );
      }
    }
    return imageData;
  }

  static getCanvasBlob(
    canvas: HTMLCanvasElement,
    ...args: Parameters<HTMLCanvasElement["toBlob"]> extends [infer _, ...infer Rest] ? Rest : never
  ): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), ...args));
  }
}

(self as any).Images = Images;
